import React, { useCallback, useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as XLSX from "xlsx";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import Dialog from "../shared/Dialog";
import DialogActions from "../DialogActions";
import { useAppContext } from "../../api/AppContext";
import { useSnackbar } from "notistack";
import { useLocation } from "react-router-dom";
import InfoTooltip from "../InfoTooltip";

const requiredInputColumns = {
  // mese: "month",
  strumento: "tool",
  titolo_ita: "title in Italian",
  descrizione_ita: "description in Italian",
  prompt_ita: "prompttext in Italian",
  titolo_eng: "title in English",
  descrizione_eng: "description in English",
  prompt_eng: "prompttext in English",
  titolo_fre: "title in French",
  descrizione_fre: "description in French",
  prompt_fre: "prompttext in French",
  titolo_esp: "title in Spanish",
  descrizione_esp: "description in Spanish",
  prompt_esp: "prompttext in Spanish",
  Tags: "tags (comma separated); can be either in Italian or English",
  // isNew: "isNew",
  // azienda: "company",
};

const initialMsg = `Info: file should have the required colums. `;
const SheetImportModal = ({ onClose, onConfirm }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dialogContentRef = useRef(null);
  const [fileUploadDisplayMessage, setFileUploadDisplayMessage] =
    useState(initialMsg);
  const [parsedData, setParsedData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { tools, tags, fetchTools, fetchTags, fetchPrompts } = useAppContext();
  const location = useLocation();
  const basePath = location.pathname.split("/")[1];

  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      if (!tools.length) {
        await fetchTools(basePath);
      }
      if (!tags.length) {
        await fetchTags(basePath);
      }
      setIsLoading(false);
    };
    initializeData();
  }, [fetchTools, fetchTags, basePath, tools.length, tags.length]);

  const formik = useFormik({
    initialValues: {
      fulldata: null,
    },
    onSubmit: async (values) => {
      // Scroll to bottom when form is submitted
      if (dialogContentRef.current) {
        // Add a small delay to ensure content is rendered before scrolling
        setTimeout(() => {
          dialogContentRef.current.scrollTo({
            top: dialogContentRef.current.scrollHeight,
            behavior: "smooth",
          });
        }, 100);
      }

      try {
        const mappedData = values.fulldata.map((row, i) => {
          // Delete ImportError field if it exists
          if ("ImportError" in row) {
            delete row.ImportError;
          }
          // First map the basic fields
          const mappedRow = {
            description: {
              en: row["descrizione_eng"]?.trim() || "",
              it: row["descrizione_ita"]?.trim() || "",
              fr: row["descrizione_fre"]?.trim() || "",
              es: row["descrizione_esp"]?.trim() || "",
            },
            isNew: true,
            isfromtypeform: false,
            prompttext: {
              en: row["prompt_eng"]?.trim() || "",
              it: row["prompt_ita"]?.trim() || "",
              fr: row["prompt_fre"]?.trim() || "",
              es: row["prompt_esp"]?.trim() || "",
            },
            // tags is ignored in create prompt; tagIds is used instead
            tags:
              row["Tags"]?.split(",").map((tag) => ({
                en: tag.trim(),
                it: tag.trim(),
              })) || [],
            title: {
              en: row["titolo_eng"]?.trim() || "",
              it: row["titolo_ita"]?.trim() || "",
              fr: row["titolo_fre"]?.trim() || "",
              es: row["titolo_esp"]?.trim() || "",
            },
            toolname: row["strumento"]?.trim() || "",
          };

          // Find tool ID by name
          const tool = tools.find(
            (t) => t.toolname.toLowerCase() === mappedRow.toolname.toLowerCase()
          );
          if (!tool) {
            setValidationErrors((prev) => [
              ...prev,
              {
                index: i + 1,
                message: `Tool "${mappedRow.toolname}" not found`,
              },
            ]);
            throw new Error(`Validation not passed`);
          }

          // Find tag IDs by names
          const tagIds = mappedRow.tags.map((tag) => {
            const foundTag = tags.find(
              (t) =>
                t.tagname.en.toLowerCase() === tag.en.toLowerCase() ||
                t.tagname.it.toLowerCase() === tag.it.toLowerCase()
            );
            if (!foundTag) {
              setValidationErrors((prev) => [
                ...prev,
                {
                  index: i + 1,
                  message: `Tag "${tag.it}" not found`,
                },
              ]);
              throw new Error(`Validation not passed`);
            }
            return foundTag.id;
          });

          return {
            ...mappedRow,
            tagids: tagIds,
            toolid: tool.id,
            originalRecord: row, // Store the original record
          };
        });

        const results = await Promise.allSettled(
          mappedData.map((data) => onConfirm(data))
        );

        const failedItems = results
          .map((result, index) => {
            if (result.status === "rejected") {
              return {
                ...mappedData[index].originalRecord, // Use the original record instead of mapped data
                ImportError: result.reason?.message || "Unknown error",
              };
            }
            return null;
          })
          .filter(Boolean);

        if (failedItems.length > 0) {
          enqueueSnackbar("Errored records in the output file", {
            variant: "error",
          });
          setErrors(failedItems);
          const worksheet = XLSX.utils.json_to_sheet(failedItems);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Items");
          XLSX.writeFile(
            workbook,
            `failed_imports_${new Date().toISOString()}.xlsx`
          );
          await fetchPrompts(basePath);
        } else {
          await fetchPrompts(basePath);
          onClose();
        }
      } catch (error) {
        console.error("Error processing data:", error);
        setFileUploadDisplayMessage(`Error: ${error.message}`);
      }
    },
  });

  const handleFileUpload = useCallback(
    (event) => {
      console.log("handleFileUpload");
      setFileUploadDisplayMessage("");
      setValidationErrors([]);
      setErrors([]);
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet);

            if (jsonData.length === 0) {
              setFileUploadDisplayMessage("No data found in the file");
              return;
            }

            const requiredColumns = Object.keys(requiredInputColumns);
            const missingColumns = requiredColumns.filter(
              (col) => !Object.keys(jsonData[0]).includes(col)
            );

            if (missingColumns.length > 0) {
              setFileUploadDisplayMessage(
                `Missing required columns: ${missingColumns.join(", ")}`
              );
              return;
            }

            setParsedData(jsonData);
            formik.setFieldValue("fulldata", jsonData);
            setFileUploadDisplayMessage(`${jsonData.length} records loaded`);
          } catch (error) {
            console.error("Error parsing file:", error);
            setFileUploadDisplayMessage("Error parsing file");
          }
        };
        reader.readAsArrayBuffer(file);
      }
    },
    [formik]
  );

  return (
    <Dialog fullWidth open maxWidth="md" onClose={onClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between">
          Import Prompts from Excel
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent ref={dialogContentRef}>
        {isLoading ? (
          <Box
            alignItems="center"
            display="flex"
            justifyContent="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Box alignItems="center" display="flex">
                <input
                  accept=".xlsx,.xls"
                  id="excel-file-upload"
                  style={{ display: "none" }}
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="excel-file-upload">
                  <Button
                    color="primary"
                    component="span"
                    disabled={
                      formik.isSubmitting ||
                      isLoading ||
                      errors.length > 0 ||
                      validationErrors.length > 0
                    }
                    endIcon={<InsertDriveFileIcon />}
                    variant="outlined"
                  >
                    Load Excel File
                  </Button>
                </label>
                <div style={{ width: "10px" }}></div>
                <InfoTooltip
                  text={Object.entries(requiredInputColumns)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join("\n")}
                  title="Required Columns"
                />
              </Box>
              {fileUploadDisplayMessage && (
                <Box mt={1}>
                  <Typography color="textSecondary" variant="body2">
                    {fileUploadDisplayMessage === initialMsg ? (
                      <span>
                        {fileUploadDisplayMessage}
                        <Button
                          endIcon={<GetAppIcon />}
                          size="small"
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href =
                              "/promptsoffame/sampleNewPromptsFile.xlsx";
                            link.download = "sampleNewPromptsFile.xlsx";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          Example input file
                        </Button>
                      </span>
                    ) : (
                      fileUploadDisplayMessage
                    )}
                  </Typography>
                </Box>
              )}
            </Grid>

            {validationErrors.length > 0 && (
              <Grid item xs={12}>
                {validationErrors.map((error, i) => (
                  <Typography key={i} color="error">
                    Record {error.index}: {error.message}
                  </Typography>
                ))}
              </Grid>
            )}

            {parsedData && (
              <Grid item xs={12}>
                <TableContainer component={Paper} style={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {Object.keys(parsedData[0])
                          .filter((header) => header !== "ImportError")
                          .map((header) => (
                            <TableCell key={header}>{header}</TableCell>
                          ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedData.slice(0, 10).map((row, index) => (
                        <TableRow key={index}>
                          {Object.keys(row)
                            .filter((key) => key !== "ImportError")
                            .map((key) => (
                              <TableCell key={key}>{row[key]}</TableCell>
                            ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {parsedData.length > 10 && (
                  <Box mt={1}>
                    <Typography color="textSecondary" variant="body2">
                      Showing first 10 rows of {parsedData.length} total rows
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}

            {errors.length > 0 && (
              <Grid item xs={12}>
                <Typography color="error">
                  {errors.length} items failed to import. A file with the failed
                  items has been downloaded for inspection, change and retry.
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={
            !formik.dirty ||
            formik.isSubmitting ||
            isLoading ||
            errors.length > 0 ||
            validationErrors.length > 0
          }
          endIcon={formik.isSubmitting && <CircularProgress size={25} />}
          variant="contained"
          onClick={formik.handleSubmit}
        >
          Import
        </Button>
        <Button
          color="primary"
          disabled={formik.isSubmitting || isLoading || errors.length > 0}
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SheetImportModal;
